const DEFAULT_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

export const RECORDED_VOICE_DEFAULTS = Object.freeze({
  // Mobile Safari uses this path for complete turns. Six seconds split normal
  // sentences and reflective pauses into unrelated prompts, so use the same
  // bounded ceiling as the shared live-session policy while trailing silence
  // remains the normal, fast completion signal.
  hardTimeoutMs: 45000,
  intervalMs: 80,
  calibrationMs: 240,
  noSpeechTimeoutMs: 6000,
  minSpeechMs: 180,
  speechAttackFrames: 3,
  silenceMs: 950,
  timesliceMs: 250,
});

export function recordedVoiceMimeType(MediaRecorderCtor, preferredTypes = DEFAULT_MIME_TYPES) {
  if (typeof MediaRecorderCtor !== 'function') return '';
  return preferredTypes.find((type) => (
    typeof MediaRecorderCtor.isTypeSupported !== 'function' ||
    MediaRecorderCtor.isTypeSupported(type)
  )) || '';
}

function rmsFromAnalyser(analyser) {
  if (!analyser || typeof analyser.getByteTimeDomainData !== 'function') return null;
  const length = Number(analyser.fftSize) > 0 ? Number(analyser.fftSize) : 256;
  const samples = new Uint8Array(length);
  analyser.getByteTimeDomainData(samples);
  let energy = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const normalized = (samples[index] - 128) / 128;
    energy += normalized * normalized;
  }
  return Math.sqrt(energy / samples.length);
}

/**
 * Record one bounded browser voice turn.
 *
 * The controller intentionally owns its timers and recorder callbacks so a
 * Firefox fallback cannot leave an orphan recorder running after the UI has
 * moved to another turn. Diagnostics contain stage names and timings only;
 * audio and transcript content are never included.
 */
export function createRecordedVoiceCapture({
  stream,
  analyser = null,
  MediaRecorderCtor = globalThis.MediaRecorder,
  onStage = () => {},
  onSpeechStart = () => {},
  discardBeforeSpeech = false,
  preRollChunkLimit = 3,
  hardTimeoutMs = RECORDED_VOICE_DEFAULTS.hardTimeoutMs,
  intervalMs = RECORDED_VOICE_DEFAULTS.intervalMs,
  calibrationMs = RECORDED_VOICE_DEFAULTS.calibrationMs,
  noSpeechTimeoutMs = RECORDED_VOICE_DEFAULTS.noSpeechTimeoutMs,
  minSpeechMs = RECORDED_VOICE_DEFAULTS.minSpeechMs,
  speechAttackFrames = RECORDED_VOICE_DEFAULTS.speechAttackFrames,
  silenceMs = RECORDED_VOICE_DEFAULTS.silenceMs,
  timesliceMs = RECORDED_VOICE_DEFAULTS.timesliceMs,
  now = () => Date.now(),
  setIntervalFn = globalThis.setInterval,
  clearIntervalFn = globalThis.clearInterval,
  setTimeoutFn = globalThis.setTimeout,
  clearTimeoutFn = globalThis.clearTimeout,
} = {}) {
  if (!stream || typeof MediaRecorderCtor !== 'function') {
    throw new Error('recorded_voice_unavailable');
  }

  const mimeType = recordedVoiceMimeType(MediaRecorderCtor);
  const recorder = mimeType
    ? new MediaRecorderCtor(stream, { mimeType })
    : new MediaRecorderCtor(stream);
  const chunks = [];
  const preRoll = [];
  const startedAt = now();
  let intervalId = null;
  let deadlineId = null;
  let noSpeechDeadlineId = null;
  let settled = false;
  let heardSpeech = false;
  let speechStartedAt = 0;
  let lastVoiceAt = startedAt;
  let noiseFloor = 0.006;
  let consecutiveActiveFrames = 0;

  const emit = (stage, detail = {}) => onStage(stage, {
    elapsed_ms: Math.max(0, now() - startedAt),
    mode: 'recorded',
    ...detail,
  });

  let resolveDone;
  let rejectDone;
  const done = new Promise((resolve, reject) => {
    resolveDone = resolve;
    rejectDone = reject;
  });

  const clearTimers = () => {
    if (intervalId !== null) clearIntervalFn(intervalId);
    if (deadlineId !== null) clearTimeoutFn(deadlineId);
    if (noSpeechDeadlineId !== null) clearTimeoutFn(noSpeechDeadlineId);
    intervalId = null;
    deadlineId = null;
    noSpeechDeadlineId = null;
  };

  const stop = (reason = 'manual') => {
    if (settled || recorder.state === 'inactive') return false;
    emit('recording_stop_requested', { reason });
    recorder.stop();
    return true;
  };

  const cancel = () => {
    if (settled) return;
    settled = true;
    clearTimers();
    try {
      if (recorder.state !== 'inactive') recorder.stop();
    } catch (_) {}
    emit('recording_cancelled');
    rejectDone(Object.assign(new Error('recorded_voice_cancelled'), { name: 'AbortError' }));
  };

  recorder.ondataavailable = (event) => {
    if (!event.data?.size) return;
    if (!discardBeforeSpeech || heardSpeech) {
      chunks.push(event.data);
      return;
    }
    preRoll.push(event.data);
    while (preRoll.length > preRollChunkLimit) preRoll.shift();
  };
  recorder.onerror = () => {
    if (settled) return;
    settled = true;
    clearTimers();
    emit('recorder_error');
    rejectDone(new Error('recorded_voice_failed'));
  };
  recorder.onstop = () => {
    if (settled) return;
    settled = true;
    clearTimers();
    const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'application/octet-stream' });
    emit('recording_stopped', {
      bytes: blob.size,
      heard_speech: heardSpeech,
      vad_available: Boolean(analyser),
    });
    resolveDone({ blob, heardSpeech, durationMs: Math.max(0, now() - startedAt) });
  };
  recorder.onstart = () => emit('recording_started', {
    mime_type: recorder.mimeType || mimeType || 'application/octet-stream',
    vad_available: Boolean(analyser),
  });

  recorder.start(timesliceMs);
  intervalId = setIntervalFn(() => {
    if (settled || recorder.state !== 'recording') return;
    const rms = rmsFromAnalyser(analyser);
    if (rms === null) return;
    const current = now();
    const calibrating = current - startedAt < Math.max(0, calibrationMs);
    const nearFieldOverride = rms >= 0.035;
    if (!heardSpeech && calibrating && !nearFieldOverride) {
      // Learn the initial room bed before treating moderate sound as a person.
      // A nearby voice can still cross the explicit override immediately.
      noiseFloor = noiseFloor * 0.72 + rms * 0.28;
    }
    // Mobile automatic gain control can make a television or HVAC bed look
    // like speech at the former 0.01 threshold. Require a clearer rise above
    // the adaptive room floor and three consecutive frames before opening a
    // turn; normal near-field speech still crosses this boundary quickly.
    const activeThreshold = Math.min(0.09, Math.max(0.018, noiseFloor * 2.6));
    const sustainThreshold = Math.max(0.013, activeThreshold * 0.62);
    const active = heardSpeech
      ? rms >= sustainThreshold
      : (!calibrating || nearFieldOverride) && rms >= activeThreshold;
    consecutiveActiveFrames = active ? consecutiveActiveFrames + 1 : 0;
    if (!heardSpeech && !active) noiseFloor = noiseFloor * 0.94 + rms * 0.06;
    if (!heardSpeech && consecutiveActiveFrames >= Math.max(2, speechAttackFrames)) {
      heardSpeech = true;
      if (noSpeechDeadlineId !== null) clearTimeoutFn(noSpeechDeadlineId);
      noSpeechDeadlineId = null;
      speechStartedAt = current;
      lastVoiceAt = current;
      if (discardBeforeSpeech && preRoll.length) {
        chunks.push(...preRoll);
        preRoll.length = 0;
      }
      emit('speech_detected');
      onSpeechStart();
    } else if (heardSpeech && active) {
      lastVoiceAt = current;
    }
    if (
      heardSpeech &&
      current - speechStartedAt >= minSpeechMs &&
      current - lastVoiceAt >= silenceMs
    ) stop('silence');
  }, intervalMs);
  // If WebAudio analysis is unavailable, still upload a short bounded clip
  // instead of silently waiting twelve seconds and never reaching Whisper.
  deadlineId = setTimeoutFn(() => stop(analyser ? 'deadline' : 'vad_unavailable_deadline'), hardTimeoutMs);
  // If platform gain control makes valid speech impossible to distinguish from
  // the learned room floor, upload one short bounded clip instead of leaving
  // an iPhone apparently listening for 45 seconds. Once speech is detected,
  // the ordinary long-turn deadline remains authoritative.
  if (analyser && noSpeechTimeoutMs > 0 && noSpeechTimeoutMs < hardTimeoutMs) {
    noSpeechDeadlineId = setTimeoutFn(() => stop('speech_detection_fallback'), noSpeechTimeoutMs);
  }

  return { cancel, done, recorder, stop };
}
